import axios, { AxiosError } from 'axios';

// Determine the API URL based on the current hostname
const getApiUrl = () => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_API_URL || 'http://localhost:3001';
  } else if (hostname === 'app.audiomap.io') {
    return 'https://app.audiomap.io/api';
  } else {
    // Fallback for other environments
    return process.env.REACT_APP_API_URL || `https://${hostname}/api`;
  }
};

const API_BASE_URL = getApiUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // Start with 1 second

// Helper function to determine if we should retry
const shouldRetry = (error: AxiosError) => {
  // Don't retry auth errors or client errors (except 429)
  if (!error.response) return true; // Network errors
  const status = error.response.status;
  return status === 429 || status >= 500; // Rate limit or server errors
};

// Add retry logic
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config: any = error.config;
    
    // Initialize retry count
    if (!config.__retryCount) {
      config.__retryCount = 0;
    }
    
    // Check if we should retry
    if (config.__retryCount < MAX_RETRIES && shouldRetry(error)) {
      config.__retryCount++;
      
      // Calculate delay with exponential backoff
      const delay = RETRY_DELAY * Math.pow(2, config.__retryCount - 1);
      
      console.log(`Retrying request (attempt ${config.__retryCount}/${MAX_RETRIES}) after ${delay}ms...`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return api(config);
    }
    
    // Handle 401 - but not during login
    if (error.response?.status === 401 && !config.url?.includes('/auth/login')) {
      console.log('Authentication failed, redirecting to login...');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Handle rate limiting with user feedback
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['ratelimit-reset'] || 60;
      console.error(`Rate limited. Try again in ${retryAfter} seconds.`);
      
      // Create a more informative error
      const rateLimitError = new Error(`Too many requests. Please wait ${retryAfter} seconds and try again.`);
      return Promise.reject(rateLimitError);
    }
    
    return Promise.reject(error);
  }
);

// Auth endpoints
export const auth = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (data: any) =>
    api.post('/auth/register', data),
  validateToken: () =>
    api.get('/auth/validate'),
  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    window.location.href = '/login';
  },
};

// Venue endpoints
export const venues = {
  list: () => api.get('/venues'),
  get: (id: string) => api.get(`/venues/${id}`),
  create: (data: any) => api.post('/venues', data),
  update: (id: string, data: any) => api.put(`/venues/${id}`, data),
};

// Device endpoints
export const devices = {
  list: (venueId?: string) => 
    api.get('/devices', venueId ? { params: { venue_id: venueId } } : {}),
  get: (id: string) => api.get(`/devices/${id}`),
  create: (data: any) => api.post('/devices', data),
  update: (id: string, data: any) => api.put(`/devices/${id}`, data),
  delete: (id: string) => api.delete(`/devices/${id}`),
  getApiKeys: (deviceId: string) => api.get(`/devices/${deviceId}/keys`),
  generateApiKey: (deviceId: string, data: any) => api.post(`/devices/${deviceId}/keys`, data),
  revokeApiKey: (keyId: string) => api.delete(`/keys/${keyId}`),
  updateSplLogging: (deviceId: string, enabled: boolean) => 
    api.patch(`/devices/${deviceId}/spl-logging`, { enabled }),
};

// Measurements endpoints
export const measurements = {
  getLatest: (deviceId: string, limit = 100) =>
    api.get(`/measurements/device/${deviceId}/latest`, { params: { limit } }),
  getAggregate: (deviceId: string, interval: string, startTime?: string, endTime?: string) =>
    api.get(`/measurements/device/${deviceId}/aggregate`, {
      params: { interval, startTime, endTime },
    }),
  getLive: (deviceId: string) =>
    api.get(`/measurements/device/${deviceId}/latest`, { params: { limit: 60 } }),
  getHistorical: (deviceId: string, startTime: string, endTime: string) =>
    api.get(`/measurements/device/${deviceId}/range`, { 
      params: { start_time: startTime, end_time: endTime } 
    }),
  getComplianceStats: (deviceId: string, timeRange: string) =>
    api.get(`/measurements/device/${deviceId}/compliance`, { 
      params: { time_range: timeRange } 
    }),
  getViolations: (deviceId: string, timeRange: string) =>
    api.get(`/violations`, { 
      params: { device_id: deviceId, time_range: timeRange } 
    }),
  getThresholds: (deviceId: string) =>
    api.get(`/thresholds`, { params: { device_id: deviceId } }),
  exportData: (deviceId: string, timeRange: string, format: string) =>
    api.get(`/measurements/device/${deviceId}/export`, { 
      params: { time_range: timeRange, format },
      responseType: format === 'json' ? 'json' : 'blob'
    }),
};

// Violations endpoints
export const violations = {
  list: (filters?: any) => api.get('/violations', { params: filters }),
  resolve: (id: string) => api.put(`/violations/${id}/resolve`),
};

// User management endpoints
export const users = {
  list: () => api.get('/users'),
  get: (id: string) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  assignVenue: (userId: string, data: any) => 
    api.post(`/users/${userId}/venues`, data),
  removeVenueAccess: (userId: string, venueId: string) =>
    api.delete(`/users/${userId}/venues/${venueId}`),
  getVenueAssignments: () => api.get('/users/venue-assignments'),
};

// Thresholds endpoints
export const thresholds = {
  list: (venueId?: string) =>
    api.get('/thresholds', venueId ? { params: { venue_id: venueId } } : {}),
  create: (data: any) => api.post('/thresholds', data),
  update: (id: string, data: any) => api.put(`/thresholds/${id}`, data),
  delete: (id: string) => api.delete(`/thresholds/${id}`),
};

// Reports endpoints
export const reports = {
  generate: (venueId: string, startDate: string, endDate: string) =>
    api.get(`/reports/venue/${venueId}`, {
      params: { start_date: startDate, end_date: endDate },
    }),
  export: (venueId: string, format: string, startDate: string, endDate: string) =>
    api.get(`/reports/venue/${venueId}/export`, {
      params: { format, start_date: startDate, end_date: endDate },
      responseType: 'blob',
    }),
};

// Device Provisioning
export const provisioning = {
  getUnclaimed: () => api.get('/provisioning/unclaimed'),
  claim: (data: any) => api.post('/provisioning/claim', data),
  autoProvision: (data: any) => api.post('/provisioning/auto-provision', data),
  checkIn: (deviceId: string) => api.post('/provisioning/checkin', { deviceId }),
};

// Venue Management (Multi-tenant)
export const venueManagement = {
  getMyVenues: () => api.get('/venues'),
  createVenue: (data: any) => api.post('/venues', data),
  getVenueDetails: (venueId: string) => api.get(`/venue-management/${venueId}`),
  renameVenue: (venueId: string, name: string) => api.put(`/venue-management/${venueId}/rename`, { name }),
  updateSettings: (venueId: string, settings: any) => api.put(`/venue-management/${venueId}/settings`, { settings }),
  toggleMonitoring: (venueId: string, enabled: boolean) => api.patch(`/venues/${venueId}/monitoring`, { enabled }),
  inviteOwner: (venueId: string, data: any) => api.post(`/venue-management/${venueId}/invite-owner`, data),
  getVenueUsers: (venueId: string) => api.get(`/venue-management/${venueId}/users`),
  updateUserAccess: (venueId: string, userId: string, accessLevel: string) =>
    api.put(`/venue-management/${venueId}/users/${userId}/access`, { accessLevel }),
  removeUser: (venueId: string, userId: string) =>
    api.delete(`/venue-management/${venueId}/users/${userId}`),
};

// Storage management endpoints
export const storage = {
  // Account-based storage (for owners)
  getAccountStorage: (userId: string) =>
    api.get(`/storage/accounts/${userId}`),
  updateAccountLimit: (userId: string, limitGb: number) =>
    api.put(`/storage/accounts/${userId}/limit`, { limit_gb: limitGb }),
  // Device storage
  getDeviceStorage: (deviceId: string) =>
    api.get(`/storage/devices/${deviceId}`),
  updateDeviceLimit: (deviceId: string, limitGb: number) =>
    api.put(`/storage/devices/${deviceId}/limit`, { limit_gb: limitGb }),
  // Venue storage (deprecated - kept for backward compatibility)
  getVenueStorage: (venueId: string) =>
    api.get(`/storage/venues/${venueId}`),
  updateVenueLimit: (venueId: string, limitGb: number) =>
    api.put(`/storage/venues/${venueId}/limit`, { limit_gb: limitGb }),
};

// Recordings endpoints
export const recordings = {
  get: (id: string) => api.get(`/recordings/${id}`),
  list: (filters?: any) => api.get('/recordings', { params: filters }),
  update: (id: string, data: any) => api.put(`/recordings/${id}`, data),
  delete: (id: string) => api.delete(`/recordings/${id}`),
  createShareLink: (id: string, options: any) =>
    api.post(`/recordings/${id}/share`, options),
};

export default api;